/**
 * Static support contacts that work when the API is down.
 *
 * Prefer web compose URLs (Gmail / Outlook) over mailto: — many machines
 * have Chrome/Brave set as the mailto handler, which does not open a compose UI.
 */

export const DEFAULT_SUPPORT_EMAIL = "chatbot.bizacuity@bizacuity.ai";

export const SUPPORT_SUBJECT = "NOVA support - service issue";

export const SUPPORT_CONTACT_ITEMS = [
  { label: "US", value: "+1-470-602-7500", href: "tel:+14706027500" },
  { label: "UK", value: "+44 203 885 2950", href: "tel:+442038852950" },
  { label: "India", value: "+91 4043 111 808", href: "tel:+914043111808" },
  {
    label: "For sales enquiry",
    value: "sales@bizacuity.com",
    href: "mailto:sales@bizacuity.com",
  },
  {
    label: "For general queries",
    value: DEFAULT_SUPPORT_EMAIL,
    href: `mailto:${DEFAULT_SUPPORT_EMAIL}`,
  },
];

/** Keep query strings and mail bodies from blowing up on long API messages. */
const MAX_DIAGNOSTIC_FIELD_LEN = 400;

function clip(value, maxLen = MAX_DIAGNOSTIC_FIELD_LEN) {
  const text = String(value ?? "").trim();
  return text.length > maxLen ? text.slice(0, maxLen) : text;
}

/**
 * Build the plain-text body used in mailto / web compose / clipboard.
 * `path` is the page the user was on; `url`/`method` describe the failed request.
 */
export function buildSupportDiagnosticBody({
  kind = "",
  status = "",
  requestId = "",
  path = "",
  message = "",
  code = "",
  method = "",
  url = "",
  timestamp = new Date().toISOString(),
} = {}) {
  const request = [method, url].filter(Boolean).join(" ");
  return [
    "Please describe what you were doing:",
    "",
    "",
    "--- Diagnostic info (do not remove) ---",
    `Timestamp: ${timestamp}`,
    path ? `Page: ${path}` : null,
    kind ? `Error kind: ${kind}` : null,
    status ? `HTTP status: ${status}` : null,
    code ? `Error code: ${code}` : null,
    request ? `Failed request: ${request}` : null,
    requestId ? `Request ID: ${requestId}` : null,
    message ? `Message: ${message}` : null,
  ]
    .filter(Boolean)
    .join("\n");
}

/**
 * Diagnostics for a classified API error, including the page the user was on
 * and the request that failed. Shared by the error dialog and the support link.
 */
export function buildErrorDiagnostics(detail = {}) {
  return {
    kind: detail.kind || "",
    status: detail.status ?? "",
    requestId: detail.requestId || "",
    code: detail.code || "",
    method: detail.method || "",
    url: clip(detail.url),
    message: clip(detail.message),
    path:
      detail.path ||
      (typeof window !== "undefined"
        ? window.location.pathname + window.location.search
        : ""),
    timestamp: detail.timestamp || new Date().toISOString(),
  };
}

/**
 * Support page URL carrying the full diagnostic context, so a report sent from
 * /support describes the original failure instead of the support page itself.
 */
export function buildSupportPageUrl(diagnostics = {}) {
  const params = new URLSearchParams();
  const entries = {
    kind: diagnostics.kind,
    status: diagnostics.status,
    request_id: diagnostics.requestId,
    code: diagnostics.code,
    method: diagnostics.method,
    url: diagnostics.url,
    message: diagnostics.message,
    page: diagnostics.path,
    ts: diagnostics.timestamp,
  };
  Object.entries(entries).forEach(([key, value]) => {
    const text = clip(value);
    if (text) params.set(key, text);
  });
  const query = params.toString();
  return query ? `/support?${query}` : "/support";
}

/**
 * Build a mailto: URL with diagnostic fields for an outage report.
 * Prefer buildSupportGmailComposeUrl when no desktop mail client is installed.
 */
export function buildSupportMailto(diagnostics = {}) {
  const subject = encodeURIComponent(SUPPORT_SUBJECT);
  const body = encodeURIComponent(buildSupportDiagnosticBody(diagnostics));
  return `mailto:${DEFAULT_SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
}

/**
 * Gmail web compose URL — opens in the browser (works without a mailto handler).
 */
export function buildSupportGmailComposeUrl(diagnostics = {}) {
  const params = new URLSearchParams({
    view: "cm",
    fs: "1",
    to: DEFAULT_SUPPORT_EMAIL,
    su: SUPPORT_SUBJECT,
    body: buildSupportDiagnosticBody(diagnostics),
  });
  return `https://mail.google.com/mail/?${params.toString()}`;
}

/**
 * Outlook on the web compose URL — alternative for Microsoft 365 users.
 */
export function buildSupportOutlookComposeUrl(diagnostics = {}) {
  const params = new URLSearchParams({
    to: DEFAULT_SUPPORT_EMAIL,
    subject: SUPPORT_SUBJECT,
    body: buildSupportDiagnosticBody(diagnostics),
  });
  return `https://outlook.office.com/mail/deeplink/compose?${params.toString()}`;
}

/**
 * Clipboard payload: support address + full diagnostic text.
 */
export function buildSupportClipboardText(diagnostics = {}) {
  return [
    `To: ${DEFAULT_SUPPORT_EMAIL}`,
    `Subject: ${SUPPORT_SUBJECT}`,
    "",
    buildSupportDiagnosticBody(diagnostics),
  ].join("\n");
}

/**
 * Copy support To/Subject/body to the clipboard.
 * Used as a silent backup when mailto: does not open a compose UI (e.g. Brave).
 */
export async function copySupportEmailDetails(diagnostics = {}) {
  const text = buildSupportClipboardText(diagnostics);
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.left = "-9999px";
  document.body.appendChild(area);
  area.select();
  document.execCommand("copy");
  document.body.removeChild(area);
}

/** User-facing copy — no infra or provider names. */
export const SUPPORT_SEND_SUCCESS_MESSAGE = "Support email sent.";
export const SUPPORT_SEND_FAILURE_MESSAGE =
  "Couldn't send the support email. You can try again, or open your mail app below.";
